import {getStateAbbreviationByName, isValidStateName} from '@deliveriesondemand/dod-states';
import {formatPhone} from '@deliveriesondemand/dod-utils';
import moment from 'moment-timezone';

import Service from './Service';
import NavigationService from './NavigationService';
import EmploymentInfoService from './EmploymentInfoService';
import AdditionalIncomeService from './AdditionalIncomeService';
import MixpanelService from '-/services/MixpanelService';
import ServiceFaker from '-/services/ops/Faker';
import {get, set as lodashSet} from '-/util/lodash';

const CLEAR_COAPP = 'CLEAR_COAPP';
const BUYERINFO_SET = 'BUYERINFO_SET';
const BUYERINFO_SETDATA = 'BUYERINFO_SETDATA';
const BUYERINFO_COPY = 'BUYERINFO_COPY';

const reducerName = 'buyerInfo';

const initialState = {
    buyerInfo: {},
    buyerInfo_CoApp: {},
    ...ServiceFaker.buyerInfo
};

const set = (state, key, value) => {
    if (NavigationService.isCoApplicant()) {
        return {
            ...state,
            buyerInfo_CoApp: {
                ...state.buyerInfo_CoApp,
                [key]: value
            }
        };
    } else {
        return {
            ...state,
            buyerInfo: {
                ...state.buyerInfo,
                [key]: value
            }
        };
    }
};

const reducer = (state = initialState, action) => {
    const {type, payload} = action;
    switch (type) {
        case BUYERINFO_SETDATA: {
            const {buyerInfo, buyerInfo_CoApp} = payload;
            return {
                ...state,
                buyerInfo,
                buyerInfo_CoApp
            };
        }
        case BUYERINFO_SET: {
            const {key, value} = payload;
            return set(state, key, value);
        }
        case BUYERINFO_COPY: {
            const {address, homephone, cellphone, monthlyPayment, monthsAtAddress, occupancy, priorAddresses, yearsAtAddress} = payload;
            const data = {
                address,
                cellphone,
                homephone,
                monthlyPayment,
                monthsAtAddress,
                occupancy,
                priorAddresses,
                yearsAtAddress
            };

            return {
                ...state,
                buyerInfo_CoApp: {
                    ...state.buyerInfo_CoApp,
                    ...data
                }
            };
        }
        case CLEAR_COAPP: {
            return {
                ...state,
                buyerInfo_CoApp: {}
            };
        }
    }

    return state;
};

class BuyerInfoService extends Service {
    constructor() {
        super();

        if (!BuyerInfoService.instance) {
            BuyerInfoService.instance = this;
        }
        return BuyerInfoService.instance;
    }

    getReducerName() {
        return reducerName;
    }

    getReducer() {
        return reducer;
    }

    getKeyName() {
        return `buyerInfo${NavigationService.isCoApplicant() ? '_CoApp' : ''}`;
    }

    getBuyerInfoForOriginalApplicant() {
        return this.getReducerState().buyerInfo;
    }

    getBuyerInfoForCurrentApplicant() {
        return this.getReducerState()[this.getKeyName()];
    }

    set(key, value) {
        this.store.dispatch({
            type: BUYERINFO_SET,
            payload: {
                key,
                value
            }
        });
    }

    setData(data) {
        const {buyerInfo, buyerInfo_CoApp} = data;
        const dateFormat = 'MM/DD/YYYY';

        const formatData = info => {
            if (info.homephone) {
                info.homephone = formatPhone(info.homephone, true);
            }
            if (info.cellphone) {
                info.cellphone = formatPhone(info.cellphone, true);
            }
            if (info.dob) {
                info.dob = moment.utc(info.dob).format(dateFormat);
            }
            if (info.address) {
                const {state = ''} = info.address;
                if (state.length > 2 && isValidStateName(state)) {
                    info.address.state = getStateAbbreviationByName(state) || '';
                }
            }
        };

        if (buyerInfo) {
            formatData(buyerInfo);

            MixpanelService.setName(buyerInfo.name);
        }

        if (buyerInfo_CoApp) {
            formatData(buyerInfo_CoApp);
        }

        this.store.dispatch({
            type: BUYERINFO_SETDATA,
            payload: {
                buyerInfo,
                buyerInfo_CoApp
            }
        });
    }

    copyFromApplicantToCoApplicant() {
        const payload = this.getBuyerInfoForOriginalApplicant();

        this.store.dispatch({
            payload,
            type: BUYERINFO_COPY
        });
    }

    clearCoApplicant(redirect) {
        NavigationService.setData({
            coAppUnlocked: false,
            coApplicantType: undefined,
            coApplicantTypeOther: undefined,
            buyerHasCoApp: 'NO'
        });
        EmploymentInfoService.clearCoApplicant();
        AdditionalIncomeService.clearCoApplicant();

        this.store.dispatch({
            type: CLEAR_COAPP
        });

        if (redirect) {
            NavigationService.next('finalReview');
        }
    }

    getDataForSave() {
        let buyerInfo = {...this.getBuyerInfoForCurrentApplicant()};
        const defaultToZero = ['yearsAtAddress', 'monthsAtAddress'];

        if (isNaN(buyerInfo.monthlyPayment)) {
            delete buyerInfo.monthlyPayment;
        }

        defaultToZero.forEach(key => {
            const value = get(buyerInfo, key, '');
            if (!value || isNaN(value)) {
                lodashSet(buyerInfo, key, 0);
            }
        });

        buyerInfo.timezone = moment.tz.guess();
        return {[this.getKeyName()]: buyerInfo};
    }
}

export default new BuyerInfoService();
